import React, { useState, useEffect, useRef } from 'react';
import { X, Check, Link as LinkIcon, AlertCircle, RotateCcw, Move } from 'lucide-react';

export const DEFAULT_CROP = { zoom: 1, x: 0.5, y: 0.5 };

// Turns a stored crop into the CSS that frames the image the same way in the
// editor and everywhere the cover is later displayed.
export const cropStyle = (crop) => {
  const { zoom, x, y } = { ...DEFAULT_CROP, ...(crop || {}) };
  const position = `${x * 100}% ${y * 100}%`;
  return {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: position,
    transform: `scale(${zoom})`,
    transformOrigin: position,
  };
};

const clamp01 = (v) => Math.min(1, Math.max(0, v));

// Picks a link to an image the user already hosts somewhere else and lets
// them frame it — we never receive or store the file itself, only the link
// plus how it should be cropped for display.
const ImageCropModal = ({
  isOpen,
  title = 'Cover image',
  aspect = 16 / 9,
  initialUrl = '',
  initialCrop = null,
  saving = false,
  error = null,
  onCancel,
  onSave,
}) => {
  const [url, setUrl] = useState(initialUrl);
  const [crop, setCrop] = useState({ ...DEFAULT_CROP, ...(initialCrop || {}) });
  const [loadFailed, setLoadFailed] = useState(false);
  const viewportRef = useRef(null);
  const dragState = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setUrl(initialUrl || '');
    setCrop({ ...DEFAULT_CROP, ...(initialCrop || {}) });
    setLoadFailed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmed = url.trim();
  const canFrame = Boolean(trimmed) && !loadFailed;

  const handlePointerDown = (e) => {
    if (!canFrame) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = { startX: e.clientX, startY: e.clientY, origin: crop };
  };

  const handlePointerMove = (e) => {
    const state = dragState.current;
    const box = viewportRef.current;
    if (!state || !box) return;
    const rect = box.getBoundingClientRect();
    // Dragging right should reveal what's to the left of the frame, so the
    // focal point moves the opposite way to the pointer.
    const nextX = clamp01(state.origin.x - (e.clientX - state.startX) / rect.width);
    const nextY = clamp01(state.origin.y - (e.clientY - state.startY) / rect.height);
    setCrop((prev) => ({ ...prev, x: nextX, y: nextY }));
  };

  const endDrag = () => { dragState.current = null; };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <h3 className="text-sm font-bold text-foreground">{title}</h3>
          <button onClick={onCancel} className="w-8 h-8 rounded-xl hover:bg-muted flex items-center justify-center border-none bg-transparent cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <label className="text-xs font-bold text-muted-foreground mb-1.5 block">Image link</label>
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border bg-background">
              <LinkIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <input
                type="url"
                value={url}
                onChange={(e) => { setUrl(e.target.value); setLoadFailed(false); }}
                placeholder="https://example.com/cover.jpg"
                className="flex-1 bg-transparent border-none outline-none text-sm text-foreground min-w-0"
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Paste a public link to an image you already host elsewhere. It's checked automatically before it goes live.
            </p>
          </div>

          {trimmed && (
            <div>
              <div
                ref={viewportRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                style={{ aspectRatio: String(aspect) }}
                className={`relative w-full overflow-hidden rounded-xl bg-muted select-none ${canFrame ? 'cursor-move' : ''}`}
              >
                {loadFailed ? (
                  <div className="absolute inset-0 flex items-center justify-center text-[11px] text-muted-foreground px-4 text-center">
                    Couldn't load an image from that link
                  </div>
                ) : (
                  <>
                    <img
                      key={trimmed}
                      src={trimmed}
                      alt="Cover preview"
                      draggable={false}
                      onError={() => setLoadFailed(true)}
                      style={cropStyle(crop)}
                    />
                    <div className="absolute inset-0 pointer-events-none border border-white/20">
                      <div className="absolute inset-y-0 left-1/3 w-px bg-white/15" />
                      <div className="absolute inset-y-0 left-2/3 w-px bg-white/15" />
                      <div className="absolute inset-x-0 top-1/3 h-px bg-white/15" />
                      <div className="absolute inset-x-0 top-2/3 h-px bg-white/15" />
                    </div>
                  </>
                )}
              </div>

              {canFrame && (
                <>
                  <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-2">
                    <Move className="w-3 h-3" /> Drag the image to reposition it inside the frame.
                  </p>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="text-[11px] font-bold text-muted-foreground w-10">Zoom</span>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.05"
                      value={crop.zoom}
                      onChange={(e) => setCrop((prev) => ({ ...prev, zoom: Number(e.target.value) }))}
                      className="flex-1 accent-primary"
                    />
                    <button
                      onClick={() => setCrop(DEFAULT_CROP)}
                      title="Reset framing"
                      className="w-8 h-8 rounded-lg bg-muted hover:bg-muted/70 flex items-center justify-center border-none cursor-pointer flex-shrink-0"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-destructive/10 text-destructive text-xs">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-5 pb-5 pt-1 flex gap-2 flex-shrink-0">
          {initialUrl && (
            <button
              onClick={() => onSave({ url: '', crop: null })}
              disabled={saving}
              className="py-2.5 px-4 rounded-lg bg-muted text-muted-foreground hover:text-destructive text-sm font-bold border-none cursor-pointer disabled:opacity-50"
            >
              Remove
            </button>
          )}
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-lg bg-muted text-foreground text-sm font-bold hover:bg-muted/70 border-none cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ url: trimmed, crop })}
            disabled={saving || !trimmed || loadFailed}
            className="flex-1 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-50 border-none cursor-pointer flex items-center justify-center gap-2"
          >
            {saving ? 'Checking...' : <><Check className="w-4 h-4" /> Use image</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
